import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Menu, X } from "lucide-react";
import logo from "@/assets/dropycash-logo-trimmed.png";
import { useLang } from "@/i18n/LanguageProvider";
import { LanguageSwitcher } from "@/components/shared/LanguageSwitcher";
import { stripLangFromPath } from "@/i18n/config";
import { trackSignupCtaClick } from "@/lib/analytics";

export function PublicNavbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { t } = useTranslation();
  const { lp } = useLang();

  const navLinks = [
    { label: t("nav.home"), to: "/" },
    { label: t("nav.how_it_works"), to: "/about" },
    { label: t("nav.pricing"), to: "/pricing" },
    { label: t("nav.blog", "Blog"), to: "/blog" },
    { label: t("nav.faq"), to: "/faq" },
    { label: t("nav.contact"), to: "/support" },
  ];

  const currentBare = stripLangFromPath(location.pathname);

  return (
    <nav className="fixed top-0 z-50 w-full bg-[hsl(240_100%_99%)] shadow-xl shadow-violet-500/5 transition-all duration-300" aria-label={t("nav.main_navigation", "Main navigation")}>
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-2 sm:px-6">
        <Link to={lp("/")} className="shrink-0" aria-label={t("nav.home", "Home")}>
          <img
            src={logo}
            alt="DropyCash"
            width={400}
            height={274}
            decoding="async"
            className="h-10 w-auto origin-left scale-125 sm:h-12 sm:scale-150 lg:scale-[1.65]"
          />
        </Link>

        <div className="hidden items-center gap-6 font-['Plus_Jakarta_Sans'] text-sm font-medium tracking-tight lg:flex xl:gap-8">
          {navLinks.map((l) => (
            <Link
              key={l.to + l.label}
              to={lp(l.to)}
              className={
                currentBare === l.to
                  ? "border-b-2 border-violet-600 font-bold text-violet-600"
                  : "text-slate-600 transition-all duration-300 hover:scale-105 hover:text-violet-500"
              }
            >
              {l.label}
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <LanguageSwitcher />
          <Link
            to={lp("/login")}
            className="hidden px-5 py-2 text-sm font-semibold text-slate-600 transition-all hover:text-primary lg:inline-block"
          >
            {t("nav.login")}
          </Link>
          <Link
            to={lp("/signup")}
            onClick={() => trackSignupCtaClick("navbar_desktop")}
            className="hidden scale-95 rounded-full bg-primary-container px-6 py-2.5 text-sm font-bold text-on-primary shadow-lg shadow-primary/20 transition-transform hover:scale-100 active:opacity-80 lg:inline-block"
          >
            {t("nav.signup")}
          </Link>

          <button
            type="button"
            className="lg:hidden"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label={mobileOpen ? t("nav.close_menu", "Close navigation menu") : t("nav.open_menu", "Open navigation menu")}
            aria-expanded={mobileOpen}
            aria-controls="mobile-navigation"
          >
            {mobileOpen ? <X className="h-5 w-5" aria-hidden="true" /> : <Menu className="h-5 w-5" aria-hidden="true" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div id="mobile-navigation" className="bg-white p-4 lg:hidden">
          <div className="flex flex-col gap-3">
            {navLinks.map((l) => (
              <Link
                key={l.to + l.label}
                to={lp(l.to)}
                className="text-sm font-medium text-slate-600"
                onClick={() => setMobileOpen(false)}
              >
                {l.label}
              </Link>
            ))}
            <div className="flex gap-2 pt-2">
              <Link to={lp("/login")} className="flex-1 text-center text-sm font-semibold text-slate-600">
                {t("nav.login")}
              </Link>
              <Link
                to={lp("/signup")}
                onClick={() => {
                  trackSignupCtaClick("navbar_mobile");
                  setMobileOpen(false);
                }}
                className="flex-1 rounded-full bg-primary-container py-2 text-center text-sm font-bold text-on-primary"
              >
                {t("nav.signup")}
              </Link>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}
